import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Search, Save, Loader2, Check, X, RefreshCw, CheckSquare } from 'lucide-react';
import { motion } from 'framer-motion';
import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VisaRequirements() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [nationalities, setNationalities] = useState([]);
  const [fields, setFields] = useState([]);
  const [changes, setChanges] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // تصفية الجنسيات حسب البحث
  const filteredNationalities = useMemo(() => {
    return nationalities.filter(nat => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        nat.name_ar?.includes(query) ||
        nat.name_en?.toLowerCase().includes(query) ||
        nat.code?.toLowerCase().includes(query)
      );
    });
  }, [nationalities, searchQuery]);

  const getHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/visa-requirements`,
        { headers: getHeaders() }
      );
      
      setNationalities(res.data.data || []);
      setFields(res.data.fields || []);
      setChanges({});
      setHasChanges(false);
    } catch (err) {
      console.error('Error fetching visa requirements:', err);
      toast.error(language === 'ar' ? 'فشل في جلب البيانات' : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const handleCheckboxChange = (nationalityCode, fieldKey, checked) => {
    setChanges(prev => {
      const natChanges = prev[nationalityCode] || {};
      return {
        ...prev,
        [nationalityCode]: {
          ...natChanges,
          [fieldKey]: checked
        }
      };
    });
    setHasChanges(true);
  };

  const getFieldValue = (nationality, fieldKey) => {
    if (changes[nationality.code] && changes[nationality.code][fieldKey] !== undefined) {
      return changes[nationality.code][fieldKey];
    }
    return nationality.requirements?.[fieldKey] || false;
  };

  const isColumnAllSelected = (fieldKey) => {
    return filteredNationalities.every(nat => getFieldValue(nat, fieldKey));
  };

  const isColumnPartiallySelected = (fieldKey) => {
    const checkedCount = filteredNationalities.filter(nat => getFieldValue(nat, fieldKey)).length;
    return checkedCount > 0 && checkedCount < filteredNationalities.length;
  };

  const handleSelectAllColumn = (fieldKey) => {
    const allSelected = isColumnAllSelected(fieldKey);
    const newValue = !allSelected;
    
    setChanges(prev => {
      const newChanges = { ...prev };
      filteredNationalities.forEach(nat => {
        newChanges[nat.code] = {
          ...(newChanges[nat.code] || {}),
          [fieldKey]: newValue
        };
      });
      return newChanges;
    });
    setHasChanges(true);
    
    toast.success(
      language === 'ar' 
        ? (newValue ? 'تم تحديد الكل' : 'تم إلغاء تحديد الكل')
        : (newValue ? 'All selected' : 'All deselected')
    );
  };

  const saveChanges = async () => {
    if (!hasChanges) return;
    
    setSaving(true);
    try {
      const updates = Object.entries(changes).map(([nationalityCode, requirements]) => {
        const originalNat = nationalities.find(n => n.code === nationalityCode);
        const originalReqs = originalNat?.requirements || {};
        
        return {
          nationality_code: nationalityCode,
          requirements: {
            ...originalReqs,
            ...requirements
          }
        };
      });
      
      await axios.post(
        `${BACKEND_URL}/api/admin/visa-requirements/bulk-update`,
        updates,
        { headers: getHeaders() }
      );
      
      toast.success(language === 'ar' ? 'تم حفظ التغييرات بنجاح' : 'Changes saved successfully');
      fetchData();
    } catch (err) {
      console.error('Error saving changes:', err);
      toast.error(language === 'ar' ? 'فشل في حفظ التغييرات' : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const changedCount = Object.keys(changes).length;

  return (
    <div className="min-h-screen bg-[#030303]">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-purple-500/5 rounded-full blur-[150px]" />
      </div>

      {/* Header */}
      <DashboardHeader
        title={language === 'ar' ? 'التأشيرة السياحية' : 'Tourist Visa'}
        subtitle={language === 'ar' ? 'إدارة متطلبات التأشيرة لجميع الجنسيات' : 'Manage visa requirements for all nationalities'}
        icon={Globe}
        iconColor="#a855f7"
        showBackButton={true}
        backPath="/control/cloudgm"
      />

      {/* Main Content */}
      <main className="relative z-10">
        {/* Breadcrumb */}
        <div className="border-b border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <BreadcrumbNav items={[
              { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
              { label_ar: 'التحكم والمعلومات للمنصات', label_en: 'Platforms Control & Info', path: '/dashboard' },
              { label_ar: 'CLOUDGM FZ LLC (للتحكم)', label_en: 'CLOUDGM FZ LLC (Control)', path: '/dashboard' },
              { label_ar: 'التحكم والمعلومات (منصة العملاء)', label_en: 'Control & Info (Customer Platform)', path: '/control/cloudgm' },
              { label_ar: 'التأشيرة السياحية', label_en: 'Tourist Visa', path: null }
            ]} />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Search and Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 p-4 mb-6"
          >
            <div className="flex flex-wrap gap-4 items-center justify-between">
              <div className="flex-1 min-w-[250px] max-w-md">
                <div className="relative">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#71717A]" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={language === 'ar' ? 'بحث عن جنسية...' : 'Search nationality...'}
                    className="bg-white/5 border-white/10 text-white pe-10 placeholder:text-[#71717A]"
                    data-testid="visa-search-input"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <span className="text-[#71717A] text-sm">
                  {language === 'ar' 
                    ? `${filteredNationalities.length} جنسية` 
                    : `${filteredNationalities.length} nationalities`}
                </span>
                
                <Button
                  onClick={fetchData}
                  variant="outline"
                  size="sm"
                  className="border-white/10 text-white hover:bg-white/5"
                  data-testid="visa-refresh-btn"
                >
                  <RefreshCw className="h-4 w-4 me-2" />
                  {language === 'ar' ? 'تحديث' : 'Refresh'}
                </Button>

                {hasChanges && (
                  <Button
                    onClick={saveChanges}
                    disabled={saving}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="visa-save-btn"
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin me-2" />
                    ) : (
                      <Save className="h-4 w-4 me-2" />
                    )}
                    {language === 'ar' ? `حفظ (${changedCount})` : `Save (${changedCount})`}
                  </Button>
                )}
              </div>
            </div>
          </motion.div>

          {/* Table */}
          {loading ? (
            <div className="text-center py-16 bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10">
              <Loader2 className="h-8 w-8 animate-spin text-purple-500 mx-auto" />
              <p className="text-[#71717A] mt-4">
                {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
              </p>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden"
            >
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/[0.03]">
                    <tr>
                      <th className="px-4 py-4 text-right text-white font-bold sticky right-0 bg-[#0a0a0f] z-10 min-w-[200px]">
                        {language === 'ar' ? 'الجنسية' : 'Nationality'}
                      </th>
                      {fields.map((field) => {
                        const allSelected = isColumnAllSelected(field.key);
                        const partiallySelected = isColumnPartiallySelected(field.key);
                        return (
                          <th key={field.key} className="px-4 py-4 text-center text-white font-medium min-w-[120px]">
                            <div className="flex flex-col items-center gap-2">
                              <span className="text-sm">{language === 'ar' ? field.name_ar : field.name_en}</span>
                              <button
                                onClick={() => handleSelectAllColumn(field.key)}
                                data-testid={`select-all-${field.key}`}
                                className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-all ${
                                  allSelected 
                                    ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' 
                                    : partiallySelected
                                      ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'
                                      : 'bg-white/5 text-[#71717A] hover:bg-white/10'
                                }`}
                                title={language === 'ar' ? 'تحديد الكل' : 'Select All'}
                              >
                                <CheckSquare className="h-3 w-3" />
                                <span>{language === 'ar' ? 'الكل' : 'All'}</span>
                              </button>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredNationalities.map((nat, index) => {
                      const isChanged = changes[nat.code];
                      return (
                        <tr 
                          key={nat.code} 
                          className={`border-t border-white/5 hover:bg-white/[0.03] transition-colors ${
                            isChanged ? 'bg-amber-500/5' : ''
                          }`}
                        >
                          <td className="px-4 py-3 sticky right-0 bg-[#0a0a0f] z-10">
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-[#71717A] font-mono w-8">{nat.code}</span>
                              <div>
                                <p className="text-white font-medium">{nat.name_ar}</p>
                                <p className="text-xs text-[#71717A]">{nat.name_en}</p>
                              </div>
                              {isChanged && (
                                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                                  {language === 'ar' ? 'معدل' : 'Modified'}
                                </span>
                              )}
                            </div>
                          </td>
                          {fields.map((field) => {
                            const isChecked = getFieldValue(nat, field.key);
                            return (
                              <td key={field.key} className="px-4 py-3 text-center">
                                <button
                                  onClick={() => handleCheckboxChange(nat.code, field.key, !isChecked)}
                                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                                    isChecked 
                                      ? 'bg-emerald-500 text-white' 
                                      : 'bg-white/5 text-[#71717A] hover:bg-white/10'
                                  }`}
                                >
                                  {isChecked ? <Check className="h-5 w-5" /> : <X className="h-5 w-5" />}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {/* Legend */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-6 p-4 bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10"
          >
            <h3 className="text-white font-medium mb-3">
              {language === 'ar' ? 'دليل الرموز:' : 'Legend:'}
            </h3>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                  <Check className="h-5 w-5 text-white" />
                </div>
                <span className="text-[#71717A]">
                  {language === 'ar' ? 'مطلوب' : 'Required'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                  <X className="h-5 w-5 text-[#71717A]" />
                </div>
                <span className="text-[#71717A]">
                  {language === 'ar' ? 'غير مطلوب' : 'Not Required'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                  {language === 'ar' ? 'معدل' : 'Modified'}
                </span>
                <span className="text-[#71717A]">
                  {language === 'ar' ? 'تم التعديل (غير محفوظ)' : 'Modified (unsaved)'}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Info Box */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-6 p-4 bg-purple-500/10 rounded-2xl border border-purple-500/30"
          >
            <h3 className="text-purple-400 font-medium mb-2 flex items-center gap-2">
              <Globe className="h-5 w-5" />
              {language === 'ar' ? 'ملاحظة للذكاء الاصطناعي:' : 'Note for AI:'}
            </h3>
            <p className="text-[#71717A] text-sm">
              {language === 'ar' 
                ? 'هذه البيانات يتم إرسالها تلقائياً لـ Gemini في منصة المتعاملين. عند تعديل المتطلبات هنا، سيقوم الذكاء الاصطناعي بطلب المستندات المناسبة حسب جنسية العميل.'
                : 'This data is automatically sent to Gemini in the customer platform. When you modify requirements here, the AI will request appropriate documents based on customer nationality.'}
            </p>
            <p className="text-purple-400 text-sm mt-2">
              API: <code className="bg-black/30 px-2 py-1 rounded text-xs">/api/visa-requirements/{'{nationality_code}'}</code>
            </p>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
